/**
 * @file page-bg.ts
 * @description «Картинка фона» страницы во вкладке «Фон и рамка» (решение
 * пользователя 2026-10-06; документ оформления `backdrop.image`):
 * значения по умолчанию для новой картинки и пределы полей «Прозрачность» и
 * «Затемнение» числом в процентах (SB120, uiux-sb120-fields §2: ползунок
 * `NumberSliderField`, прежние ступени — подсказки). Прозрачность — обратная
 * сторона `opacity` (0 % прозрачности = opacity 1; сервер не пускает opacity < 0,1).
 */

import type { SitePageBgImage } from "@chaiberi/shared";

import type { NumberDisplay, NumberUnit } from "../site-fields/number-input";

/** Новая картинка фона: заполнить экран, без прозрачности и затемнения. */
export function newPageBg(mediaId: string): SitePageBgImage {
  return { mediaId, mode: "cover", opacity: 1, dim: 0, hideOnMobile: false };
}

export const PAGE_BG_MODE_OPTIONS: ReadonlyArray<{ value: SitePageBgImage["mode"]; label: string }> = [
  { value: "cover", label: "Заполнить экран" },
  { value: "tile", label: "Плиткой" },
  { value: "fixed", label: "Закрепить при прокрутке" },
];

/** Общие пределы полей «Затемнение» / «Прозрачность» (NumberSliderField). */
interface PercentField {
  min: number;
  max: number;
  step: number;
  unit: NumberUnit;
  display: NumberDisplay;
  presets: ReadonlyArray<{ label: string; value: number }>;
}

/** Ступени-подсказки долей: 0,2 → «20 %». */
const percentPresets = (steps: readonly number[]) => steps.map((value) => ({ label: `${Math.round(value * 100)} %`, value }));

/**
 * «Затемнение» картинки (фон страницы, фото-фон секции, обложка блога, баннер
 * категории; SB120): доля 0–0,6 (схема `dim` ≤ 0,6) показывается 0–60 %,
 * шаг 5 %; прежние ступени 0/20/40/60 — подсказки.
 */
export const DIM_FIELD: PercentField = {
  min: 0,
  max: 0.6,
  step: 0.05,
  unit: "%",
  display: { factor: 100, decimals: 0 },
  presets: percentPresets([0, 0.2, 0.4, 0.6]),
};

/**
 * «Прозрачность» картинки фона страницы: доля 0–0,9, показывается 0–90 %
 * (opacity = 1 − доля; сервер не пускает opacity < 0,1 — предел 90 %);
 * прежние ступени 0/20/40/60/80 — подсказки.
 */
export const TRANSPARENCY_FIELD: PercentField = {
  min: 0,
  max: 0.9,
  step: 0.05,
  unit: "%",
  display: { factor: 100, decimals: 0 },
  presets: percentPresets([0, 0.2, 0.4, 0.6, 0.8]),
};

/** Прозрачность (доля) для текущего `opacity`. */
export function transparencyOf(opacity: number): number {
  return Math.round((1 - opacity) * 100) / 100;
}

/** `opacity` для прозрачности (доли), не ниже 0,1 — граница сервера. */
export function opacityOf(transparency: number): number {
  return Math.max(0.1, Math.round((1 - transparency) * 100) / 100);
}
